import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

export interface RowMenuItem {
  label: string
  icon?: ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
  divider?: boolean
}

/**
 * Универсальное меню действий на строку — кнопка «⋯» с выпадающим списком.
 * Используется в списках (сайты/клиенты/бд/…).
 * Клик на кнопку останавливает всплытие, чтобы не триггерить onRowClick.
 */
export function RowMenu({ items }: { items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{top:number;left:number}>({top:0,left:0})

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !menuRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open || !btnRef.current || !menuRef.current) return
    const btn = btnRef.current.getBoundingClientRect()
    const menuW = menuRef.current.offsetWidth
    const menuH = menuRef.current.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight
    // предпочтение — под кнопкой, выровнять правым краем к кнопке
    let left = btn.right - menuW
    if (left < 8) left = 8
    if (left + menuW > vw - 8) left = vw - menuW - 8
    let top = btn.bottom + 4
    if (top + menuH > vh - 8) top = btn.top - menuH - 4
    if (top < 8) top = 8
    setPos({ top, left })
  }, [open])

  return (
    <div className="relative inline-block" ref={ref}>
      <button ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v) }}
        className="w-7 h-7 rounded-md hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900 flex items-center justify-center text-[18px] leading-none"
        aria-label="Действия"
        title="Действия"
      >
        ⋯
      </button>
      {open && (
        <div
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-50 bg-white border border-neutral-200 rounded-md shadow-lg min-w-[180px] py-1 text-[13px]"
        >
          {items.map((it, i) => it.divider ? (
            <div key={i} className="border-t border-neutral-100 my-1" />
          ) : (
            <button
              key={i}
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onClick() }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                it.disabled ? 'text-neutral-300 cursor-not-allowed'
                  : it.danger ? 'hover:bg-red-50 text-red-600'
                  : 'hover:bg-neutral-50 text-neutral-800'
              }`}
            >
              {it.icon}
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
